import type { ReactNode } from 'react';

/**
 * Primitivos visuais do painel.
 *
 * Densidade alta por decisão: a tela de folha é lida por quem confere 70
 * linhas por dia. Espaçamento generoso aqui significa rolagem — e rolagem,
 * numa conferência, significa erro que passa.
 */

export function formatarMoeda(valor: number | null | undefined): string {
  if (valor === null || valor === undefined) return '—';
  return valor.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  });
}

export function formatarNumero(valor: number | null | undefined, casas = 2): string {
  if (valor === null || valor === undefined) return '—';
  return valor.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });
}

export function Cartao({
  titulo,
  acao,
  children,
}: {
  titulo: string;
  acao?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="cartao">
      <header className="cartao__cabecalho">
        <h2>{titulo}</h2>
        {acao}
      </header>
      <div className="cartao__corpo">{children}</div>
    </section>
  );
}

export function Indicador({
  rotulo,
  valor,
  detalhe,
  tom = 'neutro',
}: {
  rotulo: string;
  valor: string;
  detalhe?: string;
  tom?: 'neutro' | 'positivo' | 'atencao' | 'critico';
}) {
  return (
    <div className={`indicador indicador--${tom}`}>
      <span className="indicador__rotulo">{rotulo}</span>
      <strong className="indicador__valor">{valor}</strong>
      {detalhe ? <span className="indicador__detalhe">{detalhe}</span> : null}
    </div>
  );
}

export function Alerta({
  severidade,
  titulo,
  children,
}: {
  severidade: 'INFO' | 'ATENCAO' | 'BLOQUEANTE';
  titulo: string;
  children?: ReactNode;
}) {
  const icone = severidade === 'BLOQUEANTE' ? '⛔' : severidade === 'ATENCAO' ? '⚠️' : 'ℹ️';
  return (
    <div className={`alerta alerta--${severidade.toLowerCase()}`}>
      <span className="alerta__icone" aria-hidden="true">
        {icone}
      </span>
      <div>
        <strong>{titulo}</strong>
        {children ? <div className="alerta__corpo">{children}</div> : null}
      </div>
    </div>
  );
}

export function Tabela({ cabecalhos, children }: { cabecalhos: string[]; children: ReactNode }) {
  return (
    <div className="tabela__envoltorio">
      <table className="tabela">
        <thead>
          <tr>
            {cabecalhos.map((c) => (
              <th key={c} className={c.startsWith('#') ? 'numerica' : undefined}>
                {c.replace(/^#/, '')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Etiqueta({ texto, tom }: { texto: string; tom: 'neutro' | 'positivo' | 'atencao' | 'critico' }) {
  return <span className={`etiqueta etiqueta--${tom}`}>{texto}</span>;
}

export function Carregando({ o }: { o: string }) {
  return <p className="carregando">Carregando {o}…</p>;
}

export function Vazio({ mensagem, sugestao }: { mensagem: string; sugestao?: string }) {
  return (
    <div className="vazio">
      <p>{mensagem}</p>
      {sugestao ? <p className="vazio__sugestao">{sugestao}</p> : null}
    </div>
  );
}

export function ErroDeCarga({ erro }: { erro: unknown }) {
  const mensagem = erro instanceof Error ? erro.message : String(erro);
  return (
    <Alerta severidade="BLOQUEANTE" titulo="Não foi possível carregar">
      {mensagem}
    </Alerta>
  );
}
